// HR-004 follow-up — Shortage Rate Rules card.
//
// Embedded on the Salary Structures page so HR/Payroll admin can set the
// ₹ amount paid to operators running 3 or 4 sides per machine type. The
// allocation drawer reads these rates as read-only — supervisor sees but
// cannot edit. Each new allocation snapshots the current rate onto the
// row, so future rate revisions never retroactively change past pay.

import { useEffect, useState } from 'react'
import {
  App, Button, Card, DatePicker, Form, Input, InputNumber, Modal, Popconfirm,
  Select, Space, Table, Tag, Typography,
} from 'antd'
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'

import {
  shortageRatesApi, type ShortageRateRule, type ShortageRateRuleCreate,
  type ShortageRateRulePatch,
} from '@/features/payroll/payrollApi'
import { MACHINE_TYPES } from '@/features/hr/manpowerApi'
import { usePermission } from '@/shared/hooks/usePermission'
import { usePlantId } from '@/shared/hooks/usePlantId'

const { Text } = Typography

export default function ShortageRatesCard() {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const { plantId } = usePlantId()
  const canEdit = usePermission('employee.create')
  const [createOpen, setCreateOpen] = useState(false)
  const [editingRule, setEditingRule] = useState<ShortageRateRule | null>(null)
  const [includeInactive, setIncludeInactive] = useState(false)

  const { data: rules, isLoading } = useQuery({
    queryKey: ['shortage-rates', plantId, includeInactive],
    queryFn: () => shortageRatesApi.list({
      plant_id: plantId!, is_active: includeInactive ? undefined : true,
    }),
    enabled: !!plantId,
  })

  const invalidate = () => qc.invalidateQueries({ queryKey: ['shortage-rates'] })

  const createMut = useMutation({
    mutationFn: (body: ShortageRateRuleCreate) => shortageRatesApi.create(body),
    onSuccess: () => {
      message.success('Shortage rate added')
      invalidate(); setCreateOpen(false)
    },
    onError: (e: unknown) => {
      const err = e as { response?: { data?: { detail?: string } } }
      message.error(err?.response?.data?.detail ?? 'Save failed')
    },
  })

  const patchMut = useMutation({
    mutationFn: ({ id, body }: { id: string; body: ShortageRateRulePatch }) =>
      shortageRatesApi.patch(id, body),
    onSuccess: () => {
      message.success('Updated')
      invalidate(); setEditingRule(null)
    },
    onError: (e: unknown) => {
      const err = e as { response?: { data?: { detail?: string } } }
      message.error(err?.response?.data?.detail ?? 'Update failed')
    },
  })

  const removeMut = useMutation({
    mutationFn: (id: string) => shortageRatesApi.remove(id),
    onSuccess: () => { message.success('Deactivated'); invalidate() },
  })

  const columns = [
    {
      title: 'Machine Type', dataIndex: 'machine_type', key: 'mt',
      render: (s: string) => <Tag color="blue">{s}</Tag>,
    },
    {
      title: 'Side Count', dataIndex: 'side_count', key: 'sc',
      render: (n: number) => (
        <Tag color={n === 3 ? 'orange' : 'red'}>
          {n}-side {n === 3 ? 'shortage' : 'big shortage'}
        </Tag>
      ),
    },
    {
      title: 'Extra ₹ (total)', dataIndex: 'extra_total_amount', key: 'amt',
      render: (n: string) => (
        <Text strong style={{ fontSize: 14 }}>₹{Number(n).toFixed(2)}</Text>
      ),
    },
    {
      title: 'Per Extra Side', key: 'per_side',
      render: (_: unknown, r: ShortageRateRule) => {
        const extraSides = r.side_count - 2  // 3-side = 1 extra, 4-side = 2 extra
        const perSide = Number(r.extra_total_amount) / extraSides
        return (
          <Text type="secondary">
            ₹{perSide.toFixed(2)}/side
          </Text>
        )
      },
    },
    {
      title: 'Effective', key: 'eff',
      render: (_: unknown, r: ShortageRateRule) => (
        <Text style={{ fontSize: 12 }}>
          {dayjs(r.effective_from).format('DD MMM YY')}
          {' → '}
          {r.effective_to ? dayjs(r.effective_to).format('DD MMM YY') : 'open'}
        </Text>
      ),
    },
    {
      title: 'Status', dataIndex: 'is_active', key: 'st',
      render: (b: boolean) => (
        <Tag color={b ? 'success' : 'default'}>
          {b ? 'ACTIVE' : 'INACTIVE'}
        </Tag>
      ),
    },
    ...(canEdit ? [{
      title: '', key: 'actions',
      render: (_: unknown, r: ShortageRateRule) => (
        <Space size={2}>
          <Button size="small" type="text" icon={<EditOutlined />}
            onClick={() => setEditingRule(r)} />
          <Popconfirm
            title="Deactivate this rate?"
            description="Existing allocations stay at their snapshot rate. Only future allocations are affected."
            onConfirm={() => removeMut.mutate(r.id)}
            okText="Deactivate" okButtonProps={{ danger: true }}
          >
            <Button size="small" type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    }] : []),
  ]

  return (
    <Card
      style={{ marginTop: 16 }}
      title={
        <Space>
          <span>Shortage Rate Rules</span>
          <Tag color="processing">Payroll-set, supervisor-read-only</Tag>
        </Space>
      }
      extra={
        <Space>
          <Tag.CheckableTag
            checked={includeInactive}
            onChange={setIncludeInactive}
            style={{ padding: '2px 10px', lineHeight: '20px' }}
          >
            {includeInactive ? '✓ ' : ''}Include inactive
          </Tag.CheckableTag>
          {canEdit && (
            <Button type="primary" icon={<PlusOutlined />}
              onClick={() => setCreateOpen(true)}>
              New Rate
            </Button>
          )}
        </Space>
      }
    >
      <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
        Set the extra ₹ paid when an operator runs more than the normal 2 sides
        on a given machine type. Supervisor sees this rate as read-only when
        allocating; payroll snapshots it onto the allocation row so future
        rate revisions don't retroactively change past pay.
      </Text>

      <Table
        rowKey="id"
        loading={isLoading}
        columns={columns as never}
        dataSource={rules ?? []}
        pagination={false}
        size="small"
      />

      <CreateRateModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSubmit={(body) => createMut.mutate(body)}
        submitting={createMut.isPending}
      />
      <EditRateModal
        rule={editingRule}
        onClose={() => setEditingRule(null)}
        onSubmit={(id, body) => patchMut.mutate({ id, body })}
        submitting={patchMut.isPending}
      />
    </Card>
  )
}

// ─── Modals ─────────────────────────────────────────────────────────────

function CreateRateModal({ open, onClose, onSubmit, submitting }: {
  open: boolean
  onClose: () => void
  onSubmit: (body: ShortageRateRuleCreate) => void
  submitting: boolean
}) {
  const [form] = Form.useForm()
  const { plantId } = usePlantId()
  return (
    <Modal
      open={open} onCancel={onClose}
      title="New Shortage Rate" width={520}
      okText="Save" confirmLoading={submitting}
      onOk={() => {
        form.validateFields().then((v) => {
          if (!plantId) return
          onSubmit({
            plant_id: plantId,
            machine_type: v.machine_type,
            side_count: v.side_count,
            extra_total_amount: Number(v.extra_total_amount),
            effective_from: v.effective_from.format('YYYY-MM-DD'),
            effective_to: v.effective_to
              ? v.effective_to.format('YYYY-MM-DD') : null,
            remarks: v.remarks || null,
          })
        })
      }}
    >
      <Form form={form} layout="vertical" initialValues={{
        side_count: 3, effective_from: dayjs(),
      }}>
        <Form.Item name="machine_type" label="Machine Type"
          rules={[{ required: true }]}>
          <Select
            options={MACHINE_TYPES.map((m) => ({ label: m, value: m }))}
            placeholder="e.g. RING_FRAME"
          />
        </Form.Item>
        <Form.Item name="side_count" label="Side count"
          rules={[{ required: true }]}>
          <Select options={[
            { label: '3 sides (shortage — 1.5 machines)', value: 3 },
            { label: '4 sides (big shortage — 2 machines)', value: 4 },
          ]} />
        </Form.Item>
        <Form.Item name="extra_total_amount" label="Extra ₹ (TOTAL for this side count)"
          rules={[{ required: true }]}
          tooltip="Total extra ₹ paid per shift for running this side count. E.g. ₹50 for 3-side means operator gets +₹50 per shift they run 3 sides.">
          <InputNumber min={0} step={5} prefix="₹" style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="effective_from" label="Effective From"
          rules={[{ required: true }]}>
          <DatePicker style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="effective_to" label="Effective Till (leave empty = open-ended)">
          <DatePicker style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="remarks" label="Remarks">
          <Input.TextArea
            rows={2}
            placeholder="e.g. revised after May 2026 wage settlement"
          />
        </Form.Item>
      </Form>
    </Modal>
  )
}

function EditRateModal({ rule, onClose, onSubmit, submitting }: {
  rule: ShortageRateRule | null
  onClose: () => void
  onSubmit: (id: string, body: ShortageRateRulePatch) => void
  submitting: boolean
}) {
  const [form] = Form.useForm()
  const open = !!rule
  // useEffect re-runs when rule changes (fixed: was useState anti-pattern
  // which only ran once on mount).
  useEffect(() => {
    if (rule) {
      form.setFieldsValue({
        extra_total_amount: Number(rule.extra_total_amount),
        effective_to: rule.effective_to ? dayjs(rule.effective_to) : null,
        remarks: rule.remarks ?? '',
      })
    }
  }, [rule, form])
  return (
    <Modal
      open={open} onCancel={onClose}
      title={`Edit ${rule?.machine_type} ${rule?.side_count}-side`}
      okText="Save" confirmLoading={submitting} width={520}
      onOk={() => {
        if (!rule) return
        form.validateFields().then((v) => {
          onSubmit(rule.id, {
            extra_total_amount: Number(v.extra_total_amount),
            effective_to: v.effective_to
              ? v.effective_to.format('YYYY-MM-DD') : null,
            remarks: v.remarks || null,
          })
        })
      }}
      destroyOnClose
    >
      {rule && (
        <Form form={form} layout="vertical" initialValues={{
          extra_total_amount: Number(rule.extra_total_amount),
          effective_to: rule.effective_to ? dayjs(rule.effective_to) : null,
          remarks: rule.remarks ?? '',
        }}>
          <Form.Item name="extra_total_amount" label="Extra ₹ (TOTAL)"
            rules={[{ required: true }]}>
            <InputNumber min={0} step={5} prefix="₹" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="effective_to" label="Effective Till">
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      )}
    </Modal>
  )
}
